import { debounce, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { getDateGroupNumber, getPublisher, IPublisher } from '~/src/dash/api/console-api';
import {
  fetchIASReportPreviewData,
  fetchReportPreviewData,
  IPreviewReport,
} from '~/src/dash/apiV2/reporting/preview';
import {
  downloadIASReportData,
  downloadReportData,
  emailIASReport,
  emailReport,
} from '~/src/dash/apiV2/reporting/reportBuilder';
import {
  getReportType,
  IReportDimension,
  IReportFilter,
  IReportMetric,
  IReportType,
  isIASReportType,
  mapStringToReportTypeID,
  IReportTypePreview,
} from '~/src/dash/apiV2/reporting/reportTypes';
import { IStore } from '~/src/dash/reducers/store';
import { IHistoryProps, ISessionStateProps, IUserStateProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  parseBidErrorCode,
  parseBidWonRejectReason,
  parseCreativeReviewStatus,
  parseDestinationType,
  parseEndpointBlockedCode,
  parseEndpointErrorCode,
  parseGppConsentStatus,
  parseInventoryRouteCondition,
  parseInventoryRouteErrorCode,
  parseNoFillCode,
  parsePhiloFallback,
  parseTargetingDevice,
  parseVastErrorCode,
} from '~/src/dash/utils/graph';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IParams, parseSearch } from '~/src/dash/utils/location';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { ReportPreview } from '~/src/dash/views/reporting/Preview';
import { ReportPreviewContext } from '~/src/dash/views/reporting/Preview/context';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { IReportDataRow } from '~/src/dash/views/reporting/Preview/common';
import { usePublicaTranslation } from '~/src/dash/publicaUI';

interface IProps extends ISessionStateProps, IUserStateProps, IHistoryProps {}

export const ReportPreviewContainer = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  function ({ user, location }: IProps) {
    const { t } = usePublicaTranslation();

    const isReadAuthorized = IS_AUTHORIZED(['read_report'], user.user)?.isAuthorized;
    const isSuperUser = hasRoleSuperUser(user.user);

    const [error, setError] = useState<unknown>(null);
    const [snackbarState, setSnackbarState] = useState<any>(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
    const [queryParams, setQueryParams] = useState<Record<string, any>>({});
    const [reportTypeConfig, setReportTypeConfig] = useState<IReportTypePreview>(
      {} as IReportTypePreview,
    );
    const [publisher, setPublisher] = useState<IPublisher | null>(null);
    const [data, setData] = useState<Array<IReportDataRow>>([]);
    const [isDataOversized, setIsDataOversized] = useState(false);
    const [rawQuery, setRawQuery] = useState('');

    const isIASReport = isIASReportType(Number(queryParams?.reporttype));

    // error handler
    const onError = (err: unknown, message: string, updatedLoadState?: LoadingState): void => {
      setError(err);
      setSnackbarState('error');
      setSnackbarMessage(message);
      if (updatedLoadState) setLoadState(updatedLoadState);
    };

    // success handler
    const onSuccess = (message: string, updatedLoadState?: LoadingState) => {
      setError(null);
      setSnackbarState('success');
      setSnackbarMessage(message);
      if (updatedLoadState) setLoadState(updatedLoadState);
    };

    // loading handler
    const onLoading = (message: string, updatedLoadState?: LoadingState) => {
      setError(null);
      setSnackbarState('processing');
      setSnackbarMessage(message);
      if (updatedLoadState) setLoadState(updatedLoadState);
    };

    // copy the full url to the clipboard
    const shareLink = async () => {
      await navigator.clipboard.writeText(window.location.href);
    };

    // copy the rawQuery to the clipboard
    const copyQuery = async () => {
      await navigator.clipboard.writeText(rawQuery);
    };

    // helper function to get and format values from queryParams that are arrays
    const getQueryParamValue = (key: string): Array<string> => {
      const cleanValue = (v: string) => v.replace(/['" ]/g, '');

      // nil check
      if (isEmpty(queryParams)) return [];

      // parse the values associated with each different key
      switch (key) {
        case 'devicetypes': {
          if (!queryParams?.devicetypes) return [];
          return (
            queryParams?.devicetypes
              ?.split(',')
              ?.map(cleanValue)
              ?.flat()
              ?.map((v: string) => (isIASReport ? parseInt(v, 10) : v)) || []
          );
        }

        case 'dimensions': {
          if (!queryParams?.groupbyoptions) return [];
          return queryParams?.groupbyoptions?.split(',')?.map(cleanValue) || [];
        }

        case 'orderBys': {
          if (!queryParams?.report_order_by) return [];
          return queryParams?.report_order_by?.split(',') || [];
        }

        case 'metrics': {
          if (!queryParams?.metrics) return [];
          return queryParams?.metrics?.split(',')?.map(cleanValue) || [];
        }

        default: {
          if (!queryParams[key]) return [];
          return queryParams[key]?.split(',')?.map(cleanValue) || [];
        }
      }
    };

    // helper function to parse and format report data
    const formatData = (data: Array<any>): Array<any> => {
      const dimensions = getQueryParamValue('dimensions');
      const isGroupedByHour = dimensions.indexOf('hour') > -1;
      const isGroupedByDay = dimensions.indexOf('day') > -1;
      const isGroupedByMonth = queryParams?.groupbyoptions?.indexOf('month') > -1;
      const isGroupedByWeekOfYear = queryParams?.groupbyoptions?.indexOf('week_of_year') > -1;
      const isGroupedByHalfHour = queryParams?.groupbyoptions?.indexOf('half_hour') > -1;

      return data.map((datum) => ({
        ...datum,
        bid_error_code: parseBidErrorCode(datum.bid_error_code),
        bid_won_reject_reason: parseBidWonRejectReason(datum.bid_won_reject_reason),
        bidder_label: datum.bidder_label_name || 'Unlabeled',
        bidder_type: datum.bidder_type || '(deprecated)',
        channel_label: datum.channel_label_name || 'Unlabeled',
        country_group: datum.country_group_name || 'Ungrouped',
        destination_type: parseDestinationType(Number(datum.destination_type)),
        device_type: datum.device_type || 'Unknown Device Type',
        endpoint_error_code: parseEndpointErrorCode(Number(datum.endpoint_error_code)),
        event_endpoint_blocked_code: parseEndpointBlockedCode(Number(datum.event_endpoint_blocked_code)),
        // eslint-disable-next-line
        gpp_consent_status: parseGppConsentStatus(datum.gpp_consent_status),
        inventory_route_condition: parseInventoryRouteCondition(
          Number(datum.inventory_route_condition),
        ),
        inventory_route_error_code: parseInventoryRouteErrorCode(
          Number(datum.inventory_route_error_code),
        ),
        no_fill_reason: parseNoFillCode(datum.no_fill_reason),
        philo_fallback: parsePhiloFallback(datum.philo_fallback),
        review_status: parseCreativeReviewStatus(datum.review_status),
        stream_error_code: datum.stream_error_code || 'No Error',
        targeting_device: parseTargetingDevice(datum.targeting_device),
        vast_error_code: parseVastErrorCode(datum.vast_error_code),
        hour: isGroupedByHour ? datum.date : null,
        half_hour: isGroupedByHalfHour ? datum.date : null,
        day: isGroupedByDay ? datum.date : null,
        month: isGroupedByMonth ? datum.date : null,
        week_of_year: isGroupedByWeekOfYear ? datum.date : null,
      }));
    };

    // map IAS report data keys to the keys of the reportType metrics / dimensions
    const formatIASData = (data: Array<any>): Array<any> => {
      const dimensions = getQueryParamValue('dimensions');
      const isGroupedByHour = dimensions.indexOf('hour') > -1;
      const isGroupedByDay = dimensions.indexOf('day') > -1;
      const isGroupedByMonth = queryParams?.groupbyoptions?.indexOf('month') > -1;
      const isGroupedByWeekOfYear = queryParams?.groupbyoptions?.indexOf('week_of_year') > -1;

      return data.map((datum) => ({
        ad_block_count: datum.AdBlockCount,
        ad_block_rate: datum.AdBlockRate,
        ad_size: datum.AdSize,
        ad_unit_id: datum.AdUnitID,
        ad_unit_path: datum.AdUnitPath,
        advertiser_id: datum.AdvertiserID,
        advertiser_name: datum.AdvertiserName,
        bidder_group: datum.BidderGroup,
        bidder_type: datum.BidderType,
        campaignname: datum.CampaignName,
        creative_id: datum.CreativeID,
        creative_name: datum.CreativeName,
        date: datum.Date,
        day: isGroupedByDay ? datum.Date : null,
        eligible_ads: datum.EligibleAds,
        hour: isGroupedByHour ? datum.Date : null,
        impression_rendered: datum.RenderedImpressions,
        ivt_ads: datum.IVTAds,
        ivt_rate: datum.IVTRate,
        line_item_id: datum.LineItemID,
        order_id: datum.OrderID,
        order_name: datum.OrderName,
        measured_ads: datum.MeasuredAds,
        measured_rate: datum.MeasuredRate,
        media_type_id: datum.MediaTypeID,
        month: isGroupedByMonth ? datum.Date : null,
        placement_id: datum.PlacementID,
        publica_bidder_id: datum.PublicaBidderID,
        publica_channel_id: datum.PublicaChannelID,
        publisher_id: datum.PublisherID,
        render_rate: datum.RenderRate,
        tracked_ads: datum.TrackedAds,
        viewability_rate: datum.ViewabilityRate,
        viewable_impression: datum.ViewableImpressions,
        week_of_year: isGroupedByWeekOfYear ? datum.Date : null,
      }));
    };

    // when user toggles between "Table*" and "Query*" buttons (only for super users)
    const toggleViewQuery = () => {
      setQueryParams({
        ...queryParams,
        snowflake_debug: !queryParams.snowflake_debug,
      });
    };

    // prevent spamming "Table*" and "Query*" buttons
    const debouncedToggleViewQuery = useMemo(() => {
      return debounce(toggleViewQuery, 800);
    }, [toggleViewQuery]);

    const updateQueryParams = (newParams: IParams = {}): void => {
      setQueryParams({
        ...queryParams,
        ...newParams,
      });
    };

    // when user clicks on "Download" button, fetch the report data, then download it
    const onDownloadReport = () => {
      if (!isSuperUser) {
        track(
          'Report Preview - Download Report',
          {
            is_super_user: isSuperUser,
            report: queryParams,
          },
          user.user,
        );
      }
      const startTime = moment().unix();
      onLoading(t('ReportingSnackbarDownloadingReport'), LoadingStateLoading);
      const downloadReport = isIASReport ? downloadIASReportData : downloadReportData;
      downloadReport({
        ...queryParams,
        csv: isIASReport,
      })
        .then((blob: any) => {
          // create an invisible anchor element that triggers the file download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.rel = 'noopner';
          a.href = url;
          a.download = `${queryParams?.report_name}${isIASReport ? '.csv' : '.zip'}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          onSuccess(t('DownloadComplete'));
          const loadTime = moment().unix() - startTime;
          if (!isSuperUser) {
            track(
              'Report Preview - Download Completed',
              {
                is_super_user: isSuperUser,
                load_duration_sec: loadTime,
                report_size: blob.size,
                report: queryParams,
                event_uuid: queryParams?.event_uuid,
              },
              user.user,
            );
          }
        })
        .catch((err) => {
          onError(err, t('DownloadFailed'));
        });
    };

    // prevent spamming "Download" button
    const debouncedOnDownloadReport = useMemo(() => {
      return debounce(onDownloadReport, 800);
    }, [onDownloadReport]);

    // when user clicks on "Receive full report" button
    const onEmailReport = () => {
      onLoading(t('ReportingSnackbarSchedulingReportToBeEmailed'), LoadingStateLoading);
      const email = isIASReport
        ? emailIASReport(queryParams, queryParams.ias_body)
        : emailReport(queryParams);

      email
        .then(() => onSuccess(t('ReportingSnackbarEmailScheduledSuccess'), LoadingStateHasData))
        .catch((err) => onError(err, t('UnableToSendEmail'), LoadingStateNoData));
    };

    // prevent spamming 'Receive full report' button
    const debouncedOnEmailReport = useMemo(() => {
      return debounce(onDownloadReport, 800);
    }, [onEmailReport]);

    // fetch report's publisher data
    const fetchPublisher = (publisherID: string) => {
      getPublisher({
        query: {
          publisher_id: publisherID,
        },
      })
        .then((publisher: IPublisher) => {
          setPublisher(publisher);
        })
        .catch((err) => {
          onError(
            err,
            t('SnackbarErrorRetrievingItem', { item: t('Publisher') }),
            LoadingStateNoData,
          );
        });
    };

    // fetch reportType
    const fetchReportType = (params: { [key: string]: any }) => {
      getReportType(params?.publisher_id, mapStringToReportTypeID[params?.reporttype], true)
        .then((reportType: IReportType) => {
          setReportTypeConfig({
            ...reportType,
            Dimensions: reportType?.Dimensions.reduce(
              (acc: Record<string, IReportDimension>, dimension: IReportDimension) => {
                acc[String(dimension.Key)] = dimension;
                return acc;
              },
              {} as Record<string, IReportDimension>,
            ),
            Metrics: reportType?.Metrics.reduce(
              (acc: { [key: string]: IReportMetric }, metric: IReportMetric) => {
                acc[metric.Key] = metric;
                return acc;
              },
              {},
            ),
            Filters: reportType?.Filters.reduce(
              (acc: { [key: string]: IReportFilter }, filter: IReportFilter) => {
                acc[filter.Key] = filter;
                return acc;
              },
              {},
            ),
          });
        })
        .catch((err) => {
          onError(err, t('SnackbarErrorRetrievingItem', { item: t('Report') }), LoadingStateNoData);
        });
    };

    // fetch report data
    const fetchData = (params: { [key: string]: any }) => {
      const startTime = moment().unix();
      setLoadState(LoadingStateLoading);

      const fetchReport = isIASReport
        ? fetchIASReportPreviewData(params?.publisher_id, JSON.parse(params?.ias_body || '{}'))
        : fetchReportPreviewData(params);

      fetchReport
        .then((response: IPreviewReport) => {
          const loadTime = moment().unix() - startTime;
          unstable_batchedUpdates(() => {
            setData(isIASReport ? formatIASData(response?.data) : formatData(response?.data));
            setIsDataOversized(response?.data.length >= 5000);
            setRawQuery(response?.rawQuery);
            onSuccess('', LoadingStateHasData);
          });
          if (!isSuperUser) {
            track(
              'Report Preview - Preview Loaded',
              {
                is_super_user: isSuperUser,
                load_duration_sec: loadTime,
                report_size: JSON.stringify(response).length,
                // query info
                event_uuid: queryParams?.event_uuid,
                report_id: queryParams?.report_id,
                report_name: queryParams?.report_name,
                report_type: queryParams?.report_type,
                subject: queryParams?.subject,
                graph_id: queryParams?.graph_id,
                metrics: getQueryParamValue('metrics'),
                groupbyoptions: getQueryParamValue('dimensions'),
                start_date: queryParams?.start_date,
                end_date: queryParams?.end_date,
                report_duration_days: queryParams?.report_duration_days,
                channel_group_ids: getQueryParamValue('channel_group_ids'),
                site_ids: getQueryParamValue('site_ids'),
                bidder_ids: getQueryParamValue('bidder_ids'),
                campaign_ids: getQueryParamValue('campaign_ids'),
                segment_ids: getQueryParamValue('segment_ids'),
                network_ids: getQueryParamValue('network_ids'),
                stream_types: getQueryParamValue('stream_types'),
                devicetypes: getQueryParamValue('devicetypes'),
                biddercodes: getQueryParamValue('biddercodes'),
                countries: getQueryParamValue('countries'),
                dmas: getQueryParamValue('dmas'),
                states: getQueryParamValue('states'),
                report_limit: queryParams?.report_limit,
                report_order_by: getQueryParamValue('orderBys'),
                timezone: queryParams?.timezone,
              },
              user.user,
            );
          }
        })
        .catch((err) => {
          onError(err, t('SnackbarErrorRetrievingItem', { item: t('Report') }), LoadingStateNoData);
        });

      if (queryParams?.publisher_id !== publisher?.ID) {
        fetchPublisher(queryParams?.publisher_id);
      }
    };

    // parse the params from the url on first load
    useEffect(() => {
      const params = parseSearch(location.search);
      if (!isEmpty(params)) {
        params.grouping = String(getDateGroupNumber(params.grouping)); // map Time Range groupby (day/ hour/ week) to a number
        params.snowflake_debug = params.snowflake_debug === 'true';
        setQueryParams(params);
      }
    }, []);

    // fetch reportType whenever report_type update
    useEffect(() => {
      if (!isEmpty(queryParams) && queryParams.reporttype && isReadAuthorized) {
        fetchReportType(queryParams);
      }
    }, [queryParams?.reporttype]);

    // fetch data whenever queryParams update
    useEffect(() => {
      if (!isEmpty(queryParams) && isReadAuthorized) {
        fetchData(queryParams);
      }
    }, [queryParams]);

    return (
      <ReportPreviewContext.Provider
        value={{
          data,
          isDataOversized,
          updateQueryParams,
          getQueryParamValue,
          isReadAuthorized,
          isSuperUser,
          loadState,
          onError,
          publisher,
          queryParams,
          rawQuery,
          reportTypeConfig,
          user,
        }}
      >
        <ReportPreview
          copyQuery={copyQuery}
          downloadReport={debouncedOnDownloadReport}
          emailReport={debouncedOnEmailReport}
          shareLink={shareLink}
          toggleViewQuery={debouncedToggleViewQuery}
        />
        <SnackbarView
          open={!!snackbarMessage}
          error={error}
          onClose={() => {
            setSnackbarMessage('');
            setSnackbarState(null);
          }}
          info={snackbarState === 'info'}
          processing={snackbarState === 'processing'}
          autoHideDuration={snackbarState === 'processing' ? 300000 : 6000} // longer wait time for the snack bar when downloading report
        >
          {snackbarMessage}
        </SnackbarView>
      </ReportPreviewContext.Provider>
    );
  },
);
